import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { HttpInternalService } from 'src/app/data/service/http.service';
import { AnalyticsService } from 'src/app/core/service/analytics/analytics.service';

// SD-1137: Yellow banner shown above the main app for signed-in users whose email
// is not yet confirmed. Offers a "Resend email" action rate-limited server-side.
//
// The banner is position:fixed at the top of the viewport so it sits above the
// fixed sidebar (otherwise the sidebar overlaps it on the left, creating a gap).
// It also toggles `body.sd1137-banner-visible` so the layout shell can push the
// sidebar + content down — same pattern used by god-mode-banner.
@Component({
  selector: 'app-verification-banner',
  templateUrl: './verification-banner.component.html',
  styleUrls: ['./verification-banner.component.scss']
})
export class VerificationBannerComponent implements OnInit, OnDestroy {
  private static readonly BODY_CLASS = 'sd1137-banner-visible';
  private static readonly DISMISS_KEY = 'sd1137-banner-dismissed';

  private _visible = false;
  get visible(): boolean { return this._visible; }
  set visible(v: boolean) {
    this._visible = v;
    if (typeof document !== 'undefined') {
      document.body.classList.toggle(VerificationBannerComponent.BODY_CLASS, v);
    }
  }

  sending = false;
  cooldownSec = 0;
  message = '';
  private destroy$ = new Subject<void>();
  private cooldownInterval: any;

  constructor(
    private http: HttpInternalService,
    private analytics: AnalyticsService
  ) {}

  ngOnInit(): void {
    this.visible = this.shouldShow();
    // If we still think the user is unverified, re-check with the backend — the user
    // may have clicked the verify link on another tab and our localStorage is stale.
    if (this.visible) {
      const email = localStorage.getItem('Email');
      if (email) {
        this.http.getRequest<boolean>('Account/CheckIsEmailConfirmed', { email })
          .subscribe(
            (confirmed) => {
              if (confirmed === true) {
                localStorage.setItem('EmailConfirmed', 'true');
                this.visible = false;
              }
            },
            () => { /* ignore — banner stays visible */ }
          );
      }
    }
    window.addEventListener('storage', this.onStorageChange);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    window.removeEventListener('storage', this.onStorageChange);
    if (this.cooldownInterval) clearInterval(this.cooldownInterval);
    // Don't leave the body class stuck if the component is torn down.
    if (typeof document !== 'undefined') {
      document.body.classList.remove(VerificationBannerComponent.BODY_CLASS);
    }
  }

  private onStorageChange = () => {
    this.visible = this.shouldShow();
  };

  private shouldShow(): boolean {
    // Signed in but email not confirmed. Backend sets EmailConfirmed on the user record;
    // the login response carries it in localStorage as 'EmailConfirmed' ("true"/"false").
    const id = localStorage.getItem('Id');
    const confirmed = localStorage.getItem('EmailConfirmed');
    // Dismissal is sessionStorage-scoped so the banner returns on next login —
    // we don't want users to permanently hide a verification reminder.
    const dismissed = sessionStorage.getItem(VerificationBannerComponent.DISMISS_KEY) === 'true';
    return !!id && confirmed === 'false' && !dismissed;
  }

  dismiss(): void {
    sessionStorage.setItem(VerificationBannerComponent.DISMISS_KEY, 'true');
    this.visible = false;
  }

  resend(): void {
    if (this.sending || this.cooldownSec > 0) return;
    const email = localStorage.getItem('Email');
    if (!email) return;

    this.sending = true;
    this.message = '';
    this.http.postRequest<any>('Account/ResendVerificationEmail', JSON.stringify({ email }))
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res) => {
          this.sending = false;
          this.message = res?.status === 0
            ? 'Verification email sent. Check your inbox.'
            : (res?.message || 'Failed to resend.');
          this.startCooldown(60);
          this.analytics.capture('verification_banner_resend_clicked');
        },
        () => {
          this.sending = false;
          this.message = 'Failed to resend. Try again in a minute.';
          this.startCooldown(60);
        }
      );
  }

  private startCooldown(seconds: number): void {
    this.cooldownSec = seconds;
    this.cooldownInterval = setInterval(() => {
      this.cooldownSec--;
      if (this.cooldownSec <= 0) {
        clearInterval(this.cooldownInterval);
        this.cooldownInterval = null;
      }
    }, 1000);
  }
}
